import React from "react";
import { Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { WHATSAPP_URL, VIBER_URL, ViberGlyph } from "@/components/ContactChannels";
import { useI18n } from "@/i18n/LanguageContext";

export default function Contact() {
  const { t } = useI18n();
  return (
    <div
      className="max-w-7xl mx-auto px-6 md:px-10 pt-[110px] md:pt-[128px] pb-24"
      data-testid="contact-page"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="section-label mb-5">{t("contact.label")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
            {t("contact.title.part1")}{" "}
            <span className="italic text-[#C6A15B]">{t("contact.title.accent")}</span>.
          </h1>
          <p className="text-zinc-400 mt-6 max-w-xl text-sm leading-relaxed">
            {t("contact.intro")}
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-[1px] bg-white/5 max-w-2xl">
            <Cell
              icon={<Phone size={16} />}
              label={t("contact.cell.phone")}
              value={
                <a
                  href="tel:+306948559619"
                  className="hover:text-[#C6A15B] transition-colors"
                  data-testid="contact-phone"
                >
                  +30 694 855 9619
                </a>
              }
            />
            <Cell
              icon={<Mail size={16} />}
              label={t("contact.cell.email")}
              value={
                <a
                  href="mailto:info@elysiancars.com"
                  className="hover:text-[#C6A15B] transition-colors"
                  data-testid="contact-email"
                >
                  info@elysiancars.com
                </a>
              }
            />
            <Cell
              icon={<MessageCircle size={16} />}
              label={t("contact.cell.whatsapp")}
              value={
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#C6A15B] transition-colors"
                  data-testid="contact-whatsapp"
                >
                  {t("contact.cell.whatsapp.v")}
                </a>
              }
            />
            <Cell
              icon={<ViberGlyph size={16} />}
              label={t("contact.cell.viber")}
              value={
                <a
                  href={VIBER_URL}
                  className="hover:text-[#C6A15B] transition-colors"
                  data-testid="contact-viber"
                >
                  {t("contact.cell.viber.v")}
                </a>
              }
            />
            <Cell icon={<Clock size={16} />} label={t("contact.cell.hours")} value={t("contact.cell.hours.v")} />
          </div>
        </div>

        <div className="md:col-span-5 md:border-l md:border-white/5 md:pl-10">
          <div className="border border-white/5 bg-[#0F0F0F] p-8">
            <div className="section-label mb-3">{t("contact.side.label")}</div>
            <h3 className="font-display text-2xl text-white leading-tight">
              {t("contact.side.title")}
            </h3>
            <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{t("contact.side.body")}</p>
            <Link to="/request" className="btn-gold mt-7" data-testid="contact-request-cta">
              {t("contact.side.cta")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Cell({ icon, label, value }) {
  return (
    <div className="bg-[#0B0B0B] p-6">
      <div className="flex items-center gap-2 text-[#C6A15B]">{icon}</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-3">{label}</div>
      <div className="text-white text-base mt-1">{value}</div>
    </div>
  );
}
